import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ExternalLink, Gift, MessageCircle, Minus, Plus, ShieldCheck, ShoppingBag } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import BrandTrustStrip from '../components/BrandTrustStrip';
import { useCassemsCart } from '../contexts/CassemsCartContext';
import {
  CASSEMS_CATALOGO_IDS,
  CASSEMS_COMBO_PRECO_PAR,
  CASSEMS_LANDING,
  CASSEMS_WA_DUVIDAS_E164,
  CASSEMS_WA_DUVIDAS_LABEL,
  precoUnitarioCampanhaCassems,
} from '../content/cassemsLandingCopy';
import { ITENS_DESTAQUE, formatBRL, imagemProduto } from '../data/catalogoDestaque';
import { trackPublicEvent } from '../lib/publicAnalytics';

function whatsappHrefDuvidas(prefill: string) {
  return `https://wa.me/${CASSEMS_WA_DUVIDAS_E164}?text=${encodeURIComponent(prefill)}`;
}

function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

type Phase = 'pre' | 'active' | 'ended';

function useCampaignWindow(inicioIso: string, fimIso: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  return useMemo(() => {
    const t0 = new Date(inicioIso).getTime();
    const t1 = new Date(fimIso).getTime();
    let phase: Phase;
    if (now < t0) phase = 'pre';
    else if (now <= t1) phase = 'active';
    else phase = 'ended';

    const target = phase === 'pre' ? t0 : t1;
    const ms = phase === 'ended' ? 0 : Math.max(0, target - now);
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;

    return { phase, days, hours, mins, secs };
  }, [now, inicioIso, fimIso]);
}

function CountdownBoxes({
  days,
  hours,
  mins,
  secs,
}: {
  days: number;
  hours: number;
  mins: number;
  secs: number;
}) {
  const cells = [
    { v: pad2(days), l: 'dias' },
    { v: pad2(hours), l: 'horas' },
    { v: pad2(mins), l: 'min' },
    { v: pad2(secs), l: 'seg' },
  ];
  return (
    <div className="mt-6 flex flex-wrap justify-center gap-3 sm:gap-4">
      {cells.map((c) => (
        <div
          key={c.l}
          className="min-w-[4.5rem] rounded-2xl border border-lunins-accent/25 bg-lunins-cream px-4 py-4 text-center shadow-[0_0_24px_rgba(212,165,116,0.08)]"
        >
          <div className="font-display text-3xl font-bold tabular-nums text-lunins-ink sm:text-4xl">{c.v}</div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-lunins-muted">{c.l}</div>
        </div>
      ))}
    </div>
  );
}

export default function CassemsLanding() {
  const fechamentoToken = import.meta.env.VITE_CASSEMS_FECHAMENTO_TOKEN?.trim();
  const fechamentoHref = fechamentoToken
    ? `/fechamento?token=${encodeURIComponent(fechamentoToken)}`
    : null;

  const inicioIso =
    import.meta.env.VITE_CASSEMS_INICIO_ISO?.trim() || CASSEMS_LANDING.inicioCampanhaIso;
  const fimIso = import.meta.env.VITE_CASSEMS_FIM_ISO?.trim() || CASSEMS_LANDING.fimCampanhaIso;

  const { phase, days, hours, mins, secs } = useCampaignWindow(inicioIso, fimIso);
  const { lines, addItem, setQty, totals } = useCassemsCart();
  const cartCount = lines.reduce((a, l) => a + l.quantidade, 0);

  const catalogoItens = useMemo(
    () =>
      CASSEMS_CATALOGO_IDS.map((id) => ITENS_DESTAQUE.find((x) => x.id === id)).filter(
        (x): x is (typeof ITENS_DESTAQUE)[number] => x != null
      ),
    []
  );

  useEffect(() => {
    document.title = CASSEMS_LANDING.pageTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', CASSEMS_LANDING.metaDescription);
  }, []);

  const waDuvidas = whatsappHrefDuvidas(CASSEMS_LANDING.ctaWhatsappPrefix);

  const timerTitle =
    phase === 'pre'
      ? 'Abertura desta ação de bem-estar em'
      : phase === 'active'
        ? 'Tempo restante para encomendar com preços desta página'
        : 'Esta janela de bem-estar foi encerrada';

  function qtyInCart(id: string) {
    return lines.find((l) => l.id === id)?.quantidade ?? 0;
  }

  function addCatalogItem(item: (typeof ITENS_DESTAQUE)[number], promo: number) {
    addItem({
      id: item.id,
      nome: item.nome,
      precoUnitCheio: item.precoAVista,
      precoUnitComDesconto: promo,
      imagemArquivo: imagemProduto(item),
    });
    trackPublicEvent('cassems_add_to_cart', {
      produto_id: item.id,
      produto_nome: item.nome,
      preco_promocional: promo,
    });
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <section className="relative overflow-hidden bg-hero-mesh pb-10 pt-8 md:pb-14 md:pt-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,165,116,0.08),transparent_55%)]" />
          <div className="relative mx-auto max-w-4xl px-4 text-center">
            <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/cassems/checkout"
                className="inline-flex min-h-[44px] min-w-[44px] items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-bold text-lunins-ink ring-1 ring-white/15 transition hover:bg-white/15"
              >
                <ShoppingBag className="h-4 w-4 text-lunins-accent" />
                Carrinho{cartCount > 0 ? ` (${cartCount})` : ''}
              </Link>
              <a
                href={waDuvidas}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-700/15 px-5 py-2.5 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-700/25"
              >
                <MessageCircle className="h-4 w-4" />
                Dúvidas · {CASSEMS_WA_DUVIDAS_LABEL}
              </a>
            </div>

            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-lunins-accent">
              {CASSEMS_LANDING.hero.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-3xl font-bold leading-tight text-lunins-ink md:text-4xl">
              {CASSEMS_LANDING.hero.headline}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-left text-base leading-relaxed text-lunins-ink/80 md:text-center md:text-lg">
              {CASSEMS_LANDING.hero.tagline}
            </p>
            {phase === 'active' ? (
              <a
                href="#equipamentos"
                className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-full bg-lunins-ink px-6 py-3 text-sm font-semibold text-lunins-cream transition hover:brightness-105"
              >
                Ver equipamentos e preços
              </a>
            ) : null}

            <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-lunins-border bg-lunins-surface/60 px-5 py-5 backdrop-blur-sm">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-lunins-ink/80">
                <Clock className="h-4 w-4 text-lunins-accent" />
                {CASSEMS_LANDING.evento.label}
              </div>
              <p className="mt-1 text-center text-xs text-lunins-muted">{CASSEMS_LANDING.evento.resumoHorario}</p>
              <p className="mt-4 text-center text-sm font-medium text-lunins-ink">{timerTitle}</p>
              {phase !== 'ended' ? (
                <CountdownBoxes days={days} hours={hours} mins={mins} secs={secs} />
              ) : (
                <p className="mt-6 text-center text-sm text-amber-200/90">
                  Novas condições: fale com a Lunins pelo WhatsApp oficial.
                </p>
              )}
            </div>

            {fechamentoHref ? (
              <p className="mx-auto mt-8 max-w-xl text-center text-xs text-lunins-muted">
                Proposta individual do consultor:{' '}
                <Link
                  to={fechamentoHref}
                  className="font-medium text-lunins-accent underline-offset-2 hover:underline"
                >
                  abrir fechamento com seu link
                </Link>
                .
              </p>
            ) : null}
          </div>
        </section>

        {phase === 'active' ? (
          <section
            id="equipamentos"
            className="scroll-mt-28 border-t border-lunins-border bg-lunins-sand/70 py-12 md:py-16"
          >
            <div className="mx-auto max-w-6xl px-4 sm:px-5">
              <h2 className="text-center font-display text-xl font-bold leading-snug text-lunins-ink md:text-2xl">
                Equipamentos com preços desta campanha
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-lunins-muted">
                {CASSEMS_LANDING.catalogLead}
              </p>

              <div className="mx-auto mt-10 max-w-2xl">
                <div className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.12] via-lunins-surface/90 to-lunins-night/80 p-5 shadow-[0_0_48px_rgba(245,158,11,0.07)] ring-1 ring-amber-400/10 md:p-6">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-emerald-700/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-700/20">
                      {CASSEMS_LANDING.comboCard.badgeAutomatico}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-200">
                      <Gift className="h-6 w-6" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="font-display text-lg font-bold text-amber-100 md:text-xl">{CASSEMS_LANDING.comboCard.title}</p>
                      <p className="mt-2 text-sm leading-relaxed text-lunins-ink/80">{CASSEMS_LANDING.comboCard.body}</p>
                      <p className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                        <span className="inline-flex w-fit rounded-full bg-amber-500/15 px-4 py-2 font-display text-lg font-bold tabular-nums text-amber-100 ring-1 ring-amber-400/30">
                          Par · {formatBRL(CASSEMS_COMBO_PRECO_PAR)}
                        </span>
                        <span className="text-xs font-medium text-lunins-muted sm:max-w-[14rem]">
                          Combinação: assento + pés ou assento + plataforma
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {catalogoItens.map((item) => {
                  const promo = precoUnitarioCampanhaCassems(item.id, item.precoAVista);
                  const q = qtyInCart(item.id);
                  return (
                    <div
                      key={item.id}
                      className="flex flex-col overflow-hidden rounded-2xl border border-lunins-border bg-lunins-surface/80 shadow-lg shadow-black/20 transition hover:border-white/[0.12]"
                    >
                      {imagemProduto(item) ? (
                        <div className="aspect-[4/3] bg-lunins-cream">
                          <img
                            src={imagemProduto(item)}
                            alt={item.nome}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ) : null}
                      <div className="flex flex-1 flex-col p-4 sm:p-5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-lunins-muted">{item.categoria}</p>
                        <h3 className="mt-1 font-display text-lg font-bold leading-snug text-lunins-ink">{item.nome}</h3>
                        <p className="mt-2 line-clamp-2 text-xs text-lunins-muted">{item.destaque || item.beneficios[0]}</p>
                        <div className="mt-4 rounded-xl border border-lunins-border bg-lunins-sand/60 px-3 py-3">
                          <p className="text-[10px] font-medium uppercase tracking-wide text-lunins-muted">Referência tabela</p>
                          <p className="text-sm text-lunins-muted line-through">{formatBRL(item.precoAVista)}</p>
                          <p className="mt-0.5 font-display text-xl font-bold text-lunins-accent">{formatBRL(promo)}</p>
                          <p className="mt-1 text-[10px] text-lunins-muted">Preço promocional nesta ação</p>
                        </div>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center justify-center gap-1 rounded-full border border-lunins-border bg-lunins-cream/50 p-1 sm:justify-start">
                            <button
                              type="button"
                              aria-label="Menos"
                              className="min-h-[44px] min-w-[44px] rounded-full p-2 text-lunins-ink/80 hover:bg-lunins-sand"
                              onClick={() => setQty(item.id, q - 1)}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="min-w-[2rem] text-center text-sm font-bold text-lunins-ink">{q}</span>
                            <button
                              type="button"
                              aria-label="Mais"
                              className="min-h-[44px] min-w-[44px] rounded-full p-2 text-lunins-ink/80 hover:bg-lunins-sand"
                              onClick={() =>
                                q === 0
                                  ? addCatalogItem(item, promo)
                                  : setQty(item.id, q + 1)
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="min-h-[44px] w-full rounded-full bg-lunins-accent/20 px-4 py-2.5 text-xs font-bold text-lunins-accent ring-1 ring-lunins-accent/30 transition hover:bg-lunins-accent/30 sm:w-auto"
                            onClick={() => addCatalogItem(item, promo)}
                          >
                            + Adicionar
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {lines.length > 0 && totals.valorDescontoCombo > 0 ? (
                <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] px-4 py-4 text-center text-sm text-lunins-ink/80">
                  <p className="font-semibold text-emerald-800">Combo promocional aplicado no total</p>
                  <p className="mt-2 leading-relaxed text-lunins-muted">{totals.comboDetalhe}</p>
                  <p className="mt-2 text-xs text-emerald-700">
                    Economia de {formatBRL(totals.valorDescontoCombo)} em relação aos itens somados separadamente.
                  </p>
                </div>
              ) : null}

              <div className="mx-auto mt-10 max-w-md rounded-2xl border border-white/[0.1] bg-lunins-elevated/60 p-5 text-center shadow-inner">
                {lines.length > 0 ? (
                  <>
                    <p className="text-xs text-lunins-muted">
                      Subtotal itens (preços da ação){' '}
                      <span className="text-lunins-ink/80">{formatBRL(totals.subtotalPrecosPromo)}</span>
                    </p>
                    {totals.valorDescontoCombo > 0 ? (
                      <p className="mt-2 text-xs text-emerald-800">
                        Ajuste combo especial: −{formatBRL(totals.valorDescontoCombo)}
                      </p>
                    ) : null}
                    <p className="mt-3 text-base text-lunins-ink/80">
                      Total estimado:{' '}
                      <strong className="font-display text-lg text-lunins-ink">{formatBRL(totals.subtotalComDesconto)}</strong>
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-lunins-muted">Adicione equipamentos acima para ver o total.</p>
                )}
                <Link
                  to="/cassems/checkout"
                  onClick={() =>
                    trackPublicEvent('cta_click', {
                      origem: 'cassems_resumo',
                      alvo: '/cassems/checkout',
                      itens_no_carrinho: lines.length,
                      total_estimado: totals.subtotalComDesconto,
                    })
                  }
                  className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-lunins-ink py-3.5 text-sm font-semibold text-lunins-cream"
                >
                  Continuar para dados e PDF
                </Link>
              </div>
            </div>
          </section>
        ) : null}

        <section className="border-t border-lunins-border bg-lunins-sand/50 px-4 py-12 sm:px-5 md:py-14">
          <div className="mx-auto max-w-2xl">
            <div className="flex gap-3">
              <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-lunins-accent" aria-hidden />
              <div>
                <h2 className="font-display text-lg font-bold text-lunins-ink md:text-xl">
                  {CASSEMS_LANDING.garantiaENota.title}
                </h2>
                <ul className="mt-4 list-disc space-y-3 pl-5 text-sm leading-relaxed text-lunins-muted">
                  {CASSEMS_LANDING.garantiaENota.itens.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>

            <h2 className="mt-12 font-display text-lg font-bold text-lunins-ink md:text-xl">
              {CASSEMS_LANDING.objecoes.title}
            </h2>
            <div className="mt-4 space-y-2">
              {CASSEMS_LANDING.objecoes.itens.map((o) => (
                <details
                  key={o.pergunta}
                  className="group rounded-xl border border-lunins-border bg-lunins-surface/40 open:bg-lunins-surface/60"
                >
                  <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-lunins-ink marker:content-none [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center justify-between gap-2">
                      {o.pergunta}
                      <span className="text-lunins-accent transition group-open:rotate-180">▼</span>
                    </span>
                  </summary>
                  <p className="border-t border-lunins-border px-4 py-3 text-sm leading-relaxed text-lunins-muted">
                    {o.resposta}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-lunins-border bg-lunins-sand/20 px-4 py-12 sm:px-5 md:py-14">
          <div className="mx-auto max-w-2xl space-y-8">
            <div>
              <h2 className="font-display text-lg font-bold text-lunins-ink md:text-xl">{CASSEMS_LANDING.contextoRodape.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-lunins-muted">{CASSEMS_LANDING.contextoRodape.corpo}</p>
              <p className="mt-4 text-sm leading-relaxed text-lunins-muted">{CASSEMS_LANDING.contextoRodape.legal}</p>
            </div>
            <div className="rounded-2xl border border-lunins-border bg-lunins-surface/40 p-5">
              <h3 className="font-display text-base font-bold text-lunins-ink">{CASSEMS_LANDING.confianca.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-lunins-muted">{CASSEMS_LANDING.confianca.corpo}</p>
              <a
                href={CASSEMS_LANDING.confianca.instagramHref}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-lunins-accent underline-offset-4 hover:underline"
              >
                {CASSEMS_LANDING.confianca.instagramLabel}
                <ExternalLink className="h-3.5 w-3.5 opacity-80" aria-hidden />
              </a>
            </div>
          </div>
        </section>

        <section className="px-4 py-10 sm:px-5">
          <div className="mx-auto max-w-xl">
            <BrandTrustStrip />
          </div>
        </section>
      </main>

      <SiteFooter />

      {phase === 'active' ? (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-lunins-border bg-lunins-cream/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-md md:hidden">
          <div className="mx-auto flex max-w-lg gap-2">
            <Link
              to="/cassems/checkout"
              className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full bg-lunins-ink py-3 text-sm font-semibold text-lunins-cream"
            >
              Fechar ({cartCount})
            </Link>
            <a
              href={waDuvidas}
              target="_blank"
              rel="noreferrer"
              className="flex min-h-[48px] flex-1 items-center justify-center rounded-full bg-emerald-500 py-3 text-sm font-bold text-emerald-950"
            >
              Dúvidas
            </a>
          </div>
        </div>
      ) : null}
      {phase === 'active' ? <div className="h-[calc(4.5rem+env(safe-area-inset-bottom))] md:hidden" aria-hidden /> : null}
    </div>
  );
}
